import mapboxgl from 'mapbox-gl';

import { MAP_CONSTANTS, DEG_TO_RAD, RAD_TO_DEG, FULL_CIRCLE_DEG, GIMBAL_ROTATION_OFFSET } from './mapConstants';

export function getMidpoint(a: [number, number], b: [number, number]): [number, number] {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

export function calculateGimbalOffset(
  dronePos: [number, number],
  gimbalAngle: number,
  map: mapboxgl.Map,
  inPipMode: boolean,
  customPixelOffset?: number
): [number, number] {
  const [lng, lat] = dronePos;
  const dronePoint = map.project([lng, lat]);
  const pixelOffset = customPixelOffset ?? (inPipMode ? MAP_CONSTANTS.GIMBAL_OFFSET_PIP : MAP_CONSTANTS.GIMBAL_OFFSET_NORMAL);
  const gimbalAngleRad = gimbalAngle * DEG_TO_RAD;

  const newX = dronePoint.x + pixelOffset * Math.sin(gimbalAngleRad);
  const newY = dronePoint.y - pixelOffset * Math.cos(gimbalAngleRad);

  const newPos = map.unproject([newX, newY]);
  return [newPos.lng, newPos.lat];
}

export function calculateGeographicBearing(point1: [number, number], point2: [number, number]): number {
  const [lon1, lat1] = point1;
  const [lon2, lat2] = point2;

  const phi1 = lat1 * DEG_TO_RAD;
  const phi2 = lat2 * DEG_TO_RAD;
  const dlambda = (lon2 - lon1) * DEG_TO_RAD;

  const x = Math.sin(dlambda) * Math.cos(phi2);
  const y = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dlambda);

  const theta = Math.atan2(x, y);
  return (theta * RAD_TO_DEG + FULL_CIRCLE_DEG) % FULL_CIRCLE_DEG;
}

export function adjustBearingForMapRotation(bearing: number, mapBearing: number): number {
  return bearing - mapBearing;
}

export function adjustGimbalBearingForMapRotation(gimbalAngle: number, mapBearing: number): number {
  return gimbalAngle + GIMBAL_ROTATION_OFFSET - mapBearing;
}

export function createPaddedBounds(bounds: mapboxgl.LngLatBounds, padding: number): mapboxgl.LngLatBounds {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const lngPadding = (ne.lng - sw.lng) * padding;
  const latPadding = (ne.lat - sw.lat) * padding;

  return new mapboxgl.LngLatBounds(
    [sw.lng + lngPadding, sw.lat + latPadding],
    [ne.lng - lngPadding, ne.lat - latPadding]
  );
}

export function calculateFlyToSpeed(distance: number): number {
  return distance > MAP_CONSTANTS.FLY_TO_DISTANCE_THRESHOLD
    ? MAP_CONSTANTS.FLY_TO_SPEED_FAST
    : MAP_CONSTANTS.FLY_TO_SPEED_SLOW;
}

export function shouldUseUserZoom(createdTime: number, waitMs: number): boolean {
  return Date.now() - createdTime > waitMs;
}
